import { zodResolver } from '@hookform/resolvers/zod'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { ColumnPreset } from 'models/column_preset'
import { cn } from 'components/UI/cn'
import { Modal } from 'components/UI/Modal/Modal'

const columnPresetFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(50, 'Must be 50 characters or less'),
  shared: z.boolean(),
})

export type ColumnPresetFormData = z.infer<typeof columnPresetFormSchema>

export interface ColumnPresetModalProps {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: ColumnPresetFormData) => Promise<unknown>
  columnPresetToEdit?: ColumnPreset | null
  confirmButtonText?: string
  loading?: boolean
  title?: string
  description?: string
}

export function ColumnPresetModal({
  show,
  handleClose,
  onSubmitSuccess,
  submitApiAction,
  columnPresetToEdit,
  confirmButtonText = 'Save',
  loading = false,
  title = 'Save Column Preset',
  description = 'Create a new column preset to save this column configuration.',
}: ColumnPresetModalProps) {
  const defaultValues = useMemo((): ColumnPresetFormData => {
    if (!columnPresetToEdit) {
      return {
        name: '',
        shared: true,
      }
    }

    return {
      name: columnPresetToEdit.name,
      shared: columnPresetToEdit.shared,
    }
  }, [columnPresetToEdit])

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    setError,
    reset,
  } = useForm<ColumnPresetFormData>({
    resolver: zodResolver(columnPresetFormSchema),
    defaultValues,
    mode: 'onChange',
  })

  // Reset form when columnPresetToEdit changes
  useEffect(() => {
    if (columnPresetToEdit) {
      reset({
        name: columnPresetToEdit.name,
        shared: columnPresetToEdit.shared,
      })
    } else {
      reset({
        name: '',
        shared: true,
      })
    }
  }, [columnPresetToEdit, reset])

  const _handleClose = () => {
    reset()
    handleClose()
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitApiAction(data)
      reset()
      onSubmitSuccess?.()
      _handleClose()
    } catch (e: any) {
      if (e.response?.data?.error) {
        const errorMsg = e.response.data.error
        setError('name', { message: errorMsg })
      } else if (e.response?.data?.name) {
        setError('name', { message: e.response.data.name[0] })
      } else {
        setError('name', {
          message: `Error saving column preset: ${e.message}`,
        })
      }
    }
  })

  const isLoading = isSubmitting || loading

  const customHeader = (
    <div className="flex flex-col px-6">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-xl text-fg">{title}</h2>
        <button
          type="button"
          onClick={_handleClose}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <Cross2Icon className="h-6 w-6 text-fg" />
        </button>
      </div>
      <p className="text-sm text-fg-secondary mt-2">{description}</p>
    </div>
  )

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && _handleClose()}
      customHeader={customHeader}
      disableCloseIcon
      onAccept={onSubmit}
      acceptButtonText={confirmButtonText}
      loading={isLoading}
      blockAccept={!isValid}
      noPadding
      childrenContainerClassName="flex flex-col pt-[20px] px-6 gap-3"
      dialogFooterClassName="flex justify-center pt-4 px-6 gap-3"
      acceptButtonProps={{
        className:
          'flex-1 h-12 min-w-[150px] bg-brand hover:bg-brand-pressed rounded-md font-medium text-base text-white',
      }}
      cancelButtonProps={{
        className:
          'flex-1 h-12 bg-white border border-line rounded-md font-medium text-base text-fg-secondary hover:bg-gray-50',
      }}
    >
      <div className="flex flex-col w-full gap-1.5">
        <label className="font-medium text-base text-fg">Preset Name</label>
        <input
          {...register('name')}
          type="text"
          placeholder="Hint: make names descriptive to help find them later!"
          className={cn(
            'w-full h-12 px-4 rounded-md text-sm text-fg placeholder:text-fg-muted',
            'focus:outline-none focus:ring-1 focus:ring-brand',
            errors.name ? 'border border-red-500' : 'border border-line'
          )}
          disabled={isLoading}
        />
        {errors.name && (
          <span className="text-xs text-red-500">{errors.name.message}</span>
        )}
      </div>

      {columnPresetToEdit && columnPresetToEdit.payload.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-base leading-5 text-fg">
            <span className="font-medium">Columns</span>
            <span className="font-normal"> (view only)</span>
          </label>
          <div className="border border-line rounded-lg p-2 max-h-[300px] overflow-y-auto">
            <div className="flex flex-col gap-0.5">
              {columnPresetToEdit.payload.map((column, index) => (
                <div
                  key={column.id || index}
                  className="flex items-center px-4 h-12 bg-surface-disabled rounded"
                >
                  <span className="text-sm font-medium leading-5 text-brand-deep">
                    {column.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          {...register('shared')}
          type="checkbox"
          id="shared-checkbox"
          className="w-4 h-4 rounded border-fg-muted accent-brand cursor-pointer"
          disabled={isLoading}
        />
        <label
          htmlFor="shared-checkbox"
          className="text-sm text-fg cursor-pointer"
        >
          Share preset with team
        </label>
      </div>
    </Modal>
  )
}
